
import type { PropType } from 'vue'
import type { DataTableColumn, Sort, ToolLayoutMessages } from '../../models'
import { defineComponent } from 'vue'
import { UiProgressLinear } from '../keen-ui'
import DataTableRow from './data-table-row.component.vue'
import NoDataFound from './no-data-found.component.vue'

export default defineComponent({
  name: 'DataTable',
  components: {
    DataTableRow,
    NoDataFound,
    UiProgressLinear,
  },
  props: {
    columns: Array as PropType<Array<DataTableColumn>>,
    data: Array as PropType<Array<unknown>>,
    sort: Object as PropType<Sort>,
    select: {
      type: Object as PropType<{
        enabled: boolean
        multiple?: boolean
        excludedRowIds?: Array<string>
      }>,
      default: () => ({ enabled: false }),
    },
    selection: {
      type: Array as PropType<Array<unknown>>,
      default: () => [],
    },
    selectionCheckboxesHidden: Boolean,
    expandedChildrenIds: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
    multiChildrenExpandEnabled: Boolean,
    headerHidden: Boolean,
    highlightedRows: {
      type: Array as PropType<Array<unknown>>,
      default: () => [],
    },
    highlightedTitleDescription: String,
    rowsIdField: {
      type: String,
      default: 'id',
    },
    loading: Boolean,
    readOnly: Boolean,
    messages: {
      type: Object as PropType<Pick<ToolLayoutMessages, 'noDataFound' | 'dataFiltered'>>,
      default: () => undefined,
    },
  },
  data() {
    return {
      allDataItemsAreSelected: false,
    }
  },
  watch: {
    data: {
      handler() {
        this.allDataItemsAreSelected = false
      },
    },
  },
  methods: {
    getRowProps(dataItem?: any) {
      const isRowExcludedFromSelect = this.select.excludedRowIds?.includes(dataItem?.id) || false
      return {
        columns: this.columns,
        sort: this.sort,
        select: {
          enabled: this.select.enabled && !isRowExcludedFromSelect,
          multiple: this.select.multiple,
        },
        allDataItemsAreSelected: this.allDataItemsAreSelected,
        selection: this.selection,
        selectionCheckboxesHidden: !this.select.enabled || this.selectionCheckboxesHidden,
        hasChildren: this.getLeveledData(this.data).some(
          leveledDataItem => !!leveledDataItem.children,
        ),
        multiChildrenExpandEnabled: this.multiChildrenExpandEnabled,
        expandedChildrenIds: this.expandedChildrenIds, // TODO change to expanded boolean,
        highlightedRows: this.highlightedRows,
        rowsIdField: this.rowsIdField,
      }
    },
    getLeveledData(data: any[], level = 0): any[] {
      data.forEach((dataItem: any) => {
        dataItem.level = level
        if (!!dataItem.children && !!dataItem.children.length) {
          this.getLeveledData(dataItem.children, level + 1)
        }
      })
      return data
    },
    onExpandedChildrenChange(dataItemIds: string[]): void {
      this.$emit('update:expandedChildrenIds', dataItemIds)
    },
    onSelectAllChange(selectAll: boolean): void {
      this.allDataItemsAreSelected = selectAll
      let newSelection = [...this.selection]

      if (selectAll) {
        const itemsToAdd = this.data.filter(
          dataItem =>
            !this.select.excludedRowIds?.includes(dataItem.id) &&
            !this.selection.some(
              selected => selected[this.rowsIdField] === dataItem[this.rowsIdField],
            ),
        )
        newSelection = [...newSelection, ...itemsToAdd]
      } else {
        newSelection = newSelection.filter(
          selectedItem =>
            !this.data.some(
              dataItem => dataItem[this.rowsIdField] === selectedItem[this.rowsIdField],
            ),
        )
      }

      this.$emit('selectionChange', newSelection)
    },
    resetScroll(): void {
      this.$refs.content.scrollTop = 0
    },
  },
})
